import { useRef } from "react";

import {
  faArrowDown,
  faArrowRight,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { faRightLeft } from "@fortawesome/pro-solid-svg-icons";
import {
  FormProvider,
  useController,
  useForm,
  useFormContext,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";
import { useDebounceValue } from "usehooks-ts";

import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { Icon } from "src/base-components/Icon";
import {
  MatrixTableNode,
  MatrixTableNodeData,
} from "src/constants/NodeDataTypes";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { Buttons } from "src/matrixTableNode/Buttons";
import { Fallback } from "src/matrixTableNode/Fallback";
import { MatrixTable } from "src/matrixTableNode/MatrixTable";
import { getErrors, swapConditions } from "src/matrixTableNode/utils";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { useSubmitForm } from "src/utils/useSubmitForm";

type MatrixTableNodeEditorProps = {
  selectedNode: MatrixTableNode;
  isFullscreen: boolean;
} & NodeEditorBaseProps<MatrixTableNodeData>;

export type MatrixTableError = {
  row: number;
  col: number;
  message: string;
};

export type MatrixTableNodeForm = Pick<
  MatrixTableNodeData,
  | "row_field"
  | "column_field"
  | "result_field"
  | "column_predicates"
  | "row_predicates"
  | "grid_effects"
  | "fallback"
>;

export const Field = ({
  label,
  name,
  placeholder,
  variant,
  result = false,
  disabled = false,
  readOnly = false,
  fullWidth = false,
  errorMessage,
  dataLoc,
  searchHighlightKey,
}: {
  name:
    | "row_field.value"
    | "column_field.value"
    | "result_field.value"
    | "fallback.value";
  disabled?: boolean;
  readOnly?: boolean;
  label?: string;
  dataLoc?: string;
  placeholder?: string;
  result?: boolean;
  variant?: "cell" | "cell-expandable";
  errorMessage?: string;
  fullWidth?: boolean;
  searchHighlightKey?: string;
}) => {
  const { field } = useController<MatrixTableNodeForm>({ name });
  const isSearchHighlighted = useIsFieldHighlighted(searchHighlightKey);
  return (
    <div
      className={twJoin(
        "flex flex-col gap-2",
        fullWidth ? "flex-1" : "basis-1/3",
      )}
    >
      {label && (
        <label className="whitespace-nowrap font-inter-semibold-13px text-gray-800">
          {label}
        </label>
      )}
      <div className="[&_.cm-editor]:rounded-lg [&_.cm-scroller]:rounded-lg">
        <AutocompleteCodeInput
          bgColor={result ? "bg-green-50" : undefined}
          containerDataLoc={`matrix-table-node-editor-field-${name}`}
          dataLoc={dataLoc}
          disabled={disabled}
          error={errorMessage}
          isSearchHighlighted={isSearchHighlighted}
          placeholder={placeholder}
          readOnly={readOnly}
          value={field.value as string}
          variant={variant}
          onBlur={field.onBlur}
          onChange={field.onChange}
        >
          {errorMessage && (
            <div className="absolute right-1">
              <Icon color="text-red-600" icon={faWarning} />
            </div>
          )}
        </AutocompleteCodeInput>
      </div>
    </div>
  );
};

const DebouncedFieldValue = ({
  name,
}: {
  name: "row_field.value" | "column_field.value";
}) => {
  const { watch } = useFormContext<MatrixTableNodeForm>();
  const value = watch(name);
  const [debouncedValue] = useDebounceValue(value, 200);

  return <span>{debouncedValue}</span>;
};

export const MatrixTableNodeEditor: React.FC<MatrixTableNodeEditorProps> = ({
  selectedNode,
  isReactive,
  immutable,
  onUpdate,
  displayedError,
  isFullscreen,
}) => {
  const formMethods = useForm<MatrixTableNodeForm>({
    defaultValues: selectedNode.data,
    ...(isReactive && { values: selectedNode.data }),
  });

  useSubmitForm({
    onChange: (newValues) => onUpdate({ newData: newValues }),
    disabled: isReactive,
    previousValues: selectedNode.data,
    watch: formMethods.watch,
  });

  const handleSwapConditions = () => {
    const newValues = swapConditions(formMethods.getValues());
    formMethods.setValue("row_field", newValues.row_field);
    formMethods.setValue("column_field", newValues.column_field);
    formMethods.setValue("row_predicates", newValues.row_predicates);
    formMethods.setValue("column_predicates", newValues.column_predicates);
    formMethods.setValue("grid_effects", newValues.grid_effects);
  };

  const {
    errors,
    fallbackErrorMessage,
    rowFieldErrorMessage,
    columnFieldErrorMessage,
    resultFieldErrorMessage,
  } = getErrors(displayedError, formMethods.getValues());

  const columnFieldRef = useRef<HTMLParagraphElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableContainerHeight = tableContainerRef.current?.offsetHeight ?? 0;

  const minHeight = 50; // Minimum height to reserve for other elements
  const columnFieldHeight = Math.max(0, tableContainerHeight - minHeight);
  const scrollWidth = Number(columnFieldRef.current?.scrollWidth || 0);

  const columnFieldWidth =
    tableContainerHeight > 0 && scrollWidth > columnFieldHeight
      ? columnFieldHeight
      : scrollWidth;

  return (
    <FormProvider {...formMethods}>
      <div
        className={twJoin(
          "flex h-full flex-col p-8",
          isFullscreen && "max-h-[72vh]",
        )}
      >
        <div className="mb-10 flex gap-2.5">
          <div className="flex basis-2/3 gap-2.5">
            <Field
              dataLoc="row-condition-field"
              disabled={immutable}
              errorMessage={rowFieldErrorMessage}
              label="Condition a (row)"
              name="row_field.value"
              placeholder="data.condition_a"
              searchHighlightKey={formMethods.getValues().row_field?.id}
              fullWidth
            />

            <div className="flex items-end justify-center pb-1.5">
              <Tooltip title="Swap conditions" asChild>
                <IconButton
                  icon={faRightLeft}
                  size="2xs"
                  onClick={handleSwapConditions}
                />
              </Tooltip>
            </div>

            <Field
              dataLoc="column-condition-field"
              disabled={immutable}
              errorMessage={columnFieldErrorMessage}
              label="Condition b (column)"
              name="column_field.value"
              placeholder="data.condition_b"
              searchHighlightKey={formMethods.getValues().column_field?.id}
              fullWidth
            />
          </div>

          <div className="flex items-end px-1">
            <div className="h-[35px] w-[1px] bg-gray-300" />
          </div>

          <Field
            dataLoc="result-field"
            disabled={immutable}
            errorMessage={resultFieldErrorMessage}
            label="Result"
            name="result_field.value"
            placeholder="data.result"
            readOnly={false}
            searchHighlightKey={formMethods.getValues().result_field?.id}
            result
          />
        </div>

        <div className="pl-9">
          <div className="flex flex-1 items-center gap-1 bg-gray-50 p-1.5">
            <p className="truncate font-inter-medium-11px text-gray-500">
              <DebouncedFieldValue name="column_field.value" />
            </p>
            <Icon icon={faArrowRight} size="2xs" />
          </div>
        </div>

        <div className="mb-4.5 flex min-h-[80px] overflow-hidden">
          <div className="mt-3 flex w-8 flex-col items-center gap-1 bg-gray-50 p-1.5">
            <div
              className="max-h-full w-full truncate transition-[height] duration-200 ease-linear"
              style={{ height: columnFieldHeight }}
            >
              <p
                ref={columnFieldRef}
                className="origin-top-left translate-x-4.5 rotate-90 truncate whitespace-nowrap font-inter-medium-11px text-gray-500"
                style={{ width: columnFieldWidth }}
              >
                <DebouncedFieldValue name="row_field.value" />
              </p>
            </div>
            <Icon icon={faArrowDown} size="2xs" />
          </div>
          <MatrixTable
            errors={errors}
            readOnly={immutable}
            tableContainerRef={tableContainerRef}
          />
        </div>
        <div className="ml-10">
          <Fallback
            errorMessage={fallbackErrorMessage}
            readOnly={immutable}
            searchHighlightKey={formMethods.getValues().fallback?.id}
          />
          <Buttons disabled={immutable} tableContainerRef={tableContainerRef} />
        </div>
      </div>
    </FormProvider>
  );
};
